import { useMutation } from '@tanstack/react-query';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { useDriverStateV2 } from '../../hooks/useDriverStateV2';
import { useUserCompanies } from '../../hooks/useUserCompanies';
import { queryClient } from '../client';
import env from '../../config/env';
import { getStoredSelectedCompanyId } from '../../hooks/userCompanies.shared';

interface CreateDriverData {
  name: string;
  email: string;
  phone?: string;
  assignedTruckId?: string;
  status: 'ACTIVE' | 'ON_LEAVE';
  startDate: string;
}

const createDriver = async (
  token: string,
  companyId: string,
  data: CreateDriverData
) => {
  const response = await fetch(`${env.apiUrl}/drivers`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-company-id': companyId,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to create driver: ${response.statusText}`
    );
  }

  return await response.json();
};

export const useCreateDriver = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  const { selectedCompanyId } = useUserCompanies();

  return useMutation({
    mutationFn: async (data: CreateDriverData) => {
      const activeCompanyId =
        selectedCompanyId ?? getStoredSelectedCompanyId() ?? driver?.companyId;

      if (!activeCompanyId) {
        throw new Error('Company ID not available');
      }

      const token = await getAccessTokenSilently();
      return createDriver(token, activeCompanyId, data);
    },
    onSuccess: () => {
      // Keep every drivers list in sync (each hook uses a distinct query key)
      void queryClient.invalidateQueries({ queryKey: ['all-drivers'] });
      void queryClient.invalidateQueries({ queryKey: ['company-drivers'] });
    },
  });
};
